import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ChevronDown } from "lucide-react";
import { IMAGES, STATS, whatsappLink } from "../data/content";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fgY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden stage-spotlight"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src={IMAGES.heroStage}
          alt=""
          className="w-full h-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#08070B]/60 via-[#08070B]/70 to-[#08070B]" />
      </motion.div>

      <motion.div
        style={{ y: fgY, opacity: fade }}
        className="max-w-7xl mx-auto px-5 sm:px-8 w-full pt-32 pb-20"
      >
        <motion.p
          data-testid="hero-overline"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="font-mono-label text-xs sm:text-sm uppercase tracking-[0.35em] text-[#E6C594] mb-8"
        >
          Agenzia di Spettacolo &amp; Produzioni — Catanzaro
        </motion.p>

        <h1
          data-testid="hero-title"
          className="font-serif-display font-extrabold tracking-tight leading-[0.95] text-[15vw] sm:text-[11vw] lg:text-[8.5rem]"
        >
          <MaskedLine delay={0.35}>MUSICA</MaskedLine>
          <MaskedLine delay={0.5}>
            <span className="italic font-medium text-gold-gradient">
              &amp; Moda
            </span>
          </MaskedLine>
          <MaskedLine delay={0.65}>
            <span className="font-mono-label font-semibold text-[#D92B34] tracking-normal">
              5.0
            </span>
          </MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-2xl text-base sm:text-lg text-[#A19CAE] leading-relaxed"
        >
          Canto, gruppi musicali, ballo, moda e show di ogni genere. E dietro
          le quinte: produzioni televisive, cinematografiche e discografiche.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            data-testid="hero-whatsapp-cta"
            href={whatsappLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-2.5 rounded-full bg-[#D92B34] text-white font-semibold px-7 py-3.5 hover:bg-[#b81f27] transition-colors duration-300"
          >
            <MessageCircle
              size={18}
              className="transition-transform duration-300 group-hover:-rotate-12"
            />
            Prenota un artista
          </a>
          <a
            data-testid="hero-productions-cta"
            href="#produzioni"
            className="rounded-full border border-[#282338] text-[#F4F1EE] font-semibold px-7 py-3.5 hover:border-[#E6C594]/60 hover:text-[#E6C594] transition-colors duration-300"
          >
            Scopri le produzioni
          </a>
        </motion.div>

        <motion.div
          data-testid="hero-stats"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.45 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-[#282338] pt-8"
        >
          {STATS.map((s) => (
            <div key={s.label}>
              <p className="font-serif-display text-3xl sm:text-4xl font-bold text-gold-gradient">
                {s.value}
              </p>
              <p className="mt-1 text-xs sm:text-sm text-[#A19CAE] tracking-wide">
                {s.label}
              </p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 float-slow text-[#A19CAE]"
      >
        <ChevronDown size={22} />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="hidden xl:block absolute right-16 top-1/3 w-64 rotate-3"
      >
        <div className="rounded-2xl overflow-hidden border border-[#E6C594]/30 shadow-[0_40px_80px_-30px_rgba(230,197,148,0.25)]">
          <img
            src={IMAGES.heroPerformer}
            alt="Performer sul palco in controluce"
            className="w-full h-80 object-cover"
          />
        </div>
        <p className="font-mono-label text-[10px] tracking-[0.3em] text-[#E6C594]/80 mt-3 text-center">
          LIVE — STAGIONE 2026
        </p>
      </motion.div>
    </section>
  );
};
